/**
 * Search metadata for the public pages — one place that knows the canonical site URL, the
 * per-page titles and descriptions (marketing-copy.ts and i18n `marketing.seo`), the Open Graph defaults and the
 * JSON-LD (schema.org) that tells search engines what Bridewell is.
 *
 * Every number in here comes from @bridewell/shared (prices, trial, withdrawal, caps): nothing
 * is written twice. Routes call `pageHead()` from their `head()`; the router merges the result
 * child-first (a page's description beats the root's) and renders it through <HeadContent />.
 * `scripts/prerender.mjs` snapshots these pages after render, so crawlers and link previews get
 * the same tags without running JavaScript.
 */
import { BILLING, DEFAULT_CHECKLIST, DEFAULT_TIMELINE, GB, LIST_PRICES_USD, OPERATOR, PACK_WEDDINGS, PLAN_LIMITS, RETENTION, type PurchasablePlanCode } from "@bridewell/shared";
import { t, type TKey } from "@/i18n";
import { LEGAL } from "@/legal/config";
import { alternates, currentLocale, LOCALE_TAG, localizePath, OG_LOCALE } from "@/lib/locales";
import { snapshotName } from "@/lib/page-files";
import { CONTENT, CONTENT_PATHS, type ContentPath } from "./content/registry";
import { APP_STORE_URL, MODULES, TOOLS } from "./marketing-data";
import { t as marketingT } from "./marketing-copy";

/** Canonical origin of the site. Every absolute URL in metadata starts here, whatever host served the page. */
export const SITE = `https://${LEGAL.appHost}`;
export const OG_IMAGE = { url: `${SITE}/og.png`, width: 1200, height: 630 } as const;

export const absolute = (path: string) => `${SITE}${path === "/" ? "/" : path.replace(/\/$/, "")}`;

type Meta = Record<string, unknown>;
type LdJson = Record<string, unknown>;

/**
 * Head tags for one public page: title, description, canonical, Open Graph + Twitter, JSON-LD, and — for a page that
 * exists in every language (src/lib/locales.ts) — one `hreflang` alternate per language plus `x-default` (English).
 * `path` is the English canonical path ("/pricing"); on a translated page the canonical is its own address
 * ("/de/pricing") and the copy comes in its language, because `t()` follows the URL.
 */
export function pageHead(opts: { path: string; title: string; description: string; ld?: LdJson | LdJson[]; ogType?: "website" | "article" }) {
  const locale = currentLocale();
  const here = localizePath(opts.path, locale);
  const url = absolute(here);
  // The page's own card (its H1 and description, drawn at build time by scripts/social-cards.mjs).
  const image = `${SITE}/og/${snapshotName(here)}.jpg`;
  const versions = alternates(opts.path);
  const hreflang = versions.length > 1
    ? [...versions.map((v) => ({ rel: "alternate", hrefLang: LOCALE_TAG[v.locale], href: absolute(v.path) })), { rel: "alternate", hrefLang: "x-default", href: absolute(opts.path) }]
    : [];
  const meta: Meta[] = [
    { title: opts.title },
    { name: "description", content: opts.description },
    { name: "apple-itunes-app", content: "app-id=6770368982" },
    { property: "og:locale", content: OG_LOCALE[locale] },
    { property: "og:type", content: opts.ogType ?? "website" },
    { property: "og:title", content: opts.title },
    { property: "og:description", content: opts.description },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:width", content: String(OG_IMAGE.width) },
    { property: "og:image:height", content: String(OG_IMAGE.height) },
    { property: "og:image:alt", content: opts.title },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: opts.description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: opts.title },
  ];
  const lds = opts.ld ? (Array.isArray(opts.ld) ? opts.ld : [opts.ld]) : [];
  for (const ld of lds) meta.push({ "script:ld+json": { "@context": "https://schema.org", ...ld } });
  return { meta, links: [{ rel: "canonical", href: url }, ...hreflang] };
}

/** Root defaults every page inherits (children override by name/property): site name, image, card type. */
export function rootHead() {
  return {
    meta: [
      { title: t("app.longName") },
      { name: "description", content: marketingT("seo.home.description") },
      { property: "og:site_name", content: t("app.name") },
      { property: "og:locale", content: "en_US" },
      { property: "og:image", content: OG_IMAGE.url },
      { property: "og:image:width", content: String(OG_IMAGE.width) },
      { property: "og:image:height", content: String(OG_IMAGE.height) },
      { property: "og:image:alt", content: t("marketing.seo.ogImageAlt") },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE.url },
      { name: "twitter:image:alt", content: t("marketing.seo.ogImageAlt") },
    ],
  };
}

/** Pages that must never be indexed (thin, personal or token-bearing): the app, sign-in, sign-up, resets, invites. */
export function noindexHead(title: string) {
  return { meta: [{ title }, { name: "robots", content: "noindex, nofollow" }] };
}

/* ───────────────────────── schema.org ───────────────────────── */

const ORG_ID = `${SITE}/#organization`;
const APP_ID = `${SITE}/#app`;

export function organizationLd(): LdJson {
  return {
    "@type": "Organization",
    "@id": ORG_ID,
    name: OPERATOR.brand,
    legalName: OPERATOR.legalName,
    identifier: OPERATOR.orgNo,
    address: OPERATOR.address,
    url: SITE,
    logo: { "@type": "ImageObject", url: `${SITE}/logo.png`, width: 256, height: 256 },
    image: OG_IMAGE.url,
    sameAs: [APP_STORE_URL],
    contactPoint: { "@type": "ContactPoint", contactType: "customer support", url: `${SITE}/contact`, email: OPERATOR.email, availableLanguage: ["English"] },
  };
}

/**
 * The site name Google shows above a result: the short brand, as og:site_name and the header say it, with the long name as
 * the alternate; `url` is the canonical home page (developers.google.com/search/docs/appearance/site-names).
 */
export function webSiteLd(): LdJson {
  return { "@type": "WebSite", "@id": `${SITE}/#website`, name: t("app.name"), alternateName: t("app.longName"), url: absolute("/"), inLanguage: LOCALE_TAG[currentLocale()], publisher: { "@id": ORG_ID } };
}

const PLAN_OFFER: { code: PurchasablePlanCode; name: TKey; interval: "month" | "year" }[] = [
  { code: "premium_monthly", name: "plan.premium", interval: "month" },
  { code: "premium_annual", name: "plan.premium", interval: "year" },
  { code: "pro_monthly", name: "plan.pro", interval: "month" },
  { code: "pro_annual", name: "plan.pro", interval: "year" },
];

/** The product: what it runs on, what it does, what it costs (USD list prices from shared, the same the pricing page falls back to). */
export function softwareApplicationLd(): LdJson {
  const pricing = absolute(localizePath("/pricing", currentLocale()));
  return {
    "@type": "SoftwareApplication",
    "@id": APP_ID,
    name: t("app.longName"),
    alternateName: t("app.name"),
    url: SITE,
    image: OG_IMAGE.url,
    description: `${marketingT("seo.home.description")} ${marketingT("platforms.availability")}`,
    applicationCategory: "LifestyleApplication",
    applicationSubCategory: "Wedding planner",
    operatingSystem: "iOS, Android, Web, macOS, visionOS",
    browserRequirements: "Requires JavaScript",
    inLanguage: LOCALE_TAG[currentLocale()],
    isAccessibleForFree: true,
    installUrl: APP_STORE_URL,
    featureList: MODULES.map((m) => t(m.blurb)),
    author: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    offers: [
      { "@type": "Offer", name: t("plan.free"), price: "0", priceCurrency: "USD", url: pricing, availability: "https://schema.org/InStock" },
      ...PLAN_OFFER.map((o) => ({
        "@type": "Offer",
        name: `${t(o.name)} (${o.interval === "year" ? t("marketing.pricing.yearly") : t("marketing.pricing.monthly")})`,
        description: t("marketing.pricing.listPriceNote"),
        price: LIST_PRICES_USD[o.code].toFixed(2),
        priceCurrency: "USD",
        url: pricing,
        availability: "https://schema.org/InStock",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: LIST_PRICES_USD[o.code].toFixed(2),
          priceCurrency: "USD",
          billingDuration: 1,
          billingIncrement: 1,
          unitCode: o.interval === "year" ? "ANN" : "MON",
        },
      })),
    ],
  };
}

/** Home → … → this page, each step at its address in the page's language where it has one. */
export function breadcrumbLd(items: { name: string; path: string }[]): LdJson {
  const locale = currentLocale();
  return {
    "@type": "BreadcrumbList",
    itemListElement: [{ name: t("marketing.seo.breadcrumbHome"), path: "/" }, ...items].map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: absolute(localizePath(it.path, locale)),
    })),
  };
}

export function contactPageLd(): LdJson {
  return { "@type": "ContactPage", name: t("marketing.seo.contact.title"), url: `${SITE}/contact`, description: t("marketing.seo.contact.description"), isPartOf: { "@id": `${SITE}/#website` } };
}

export function faqLd(items: { q: string; a: string }[]): LdJson {
  return {
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({ "@type": "Question", name: it.q, acceptedAnswer: { "@type": "Answer", text: it.a } })),
  };
}

/**
 * The pricing FAQ, as shown on /pricing. `withPayment` adds the "who handles the payment" entry the page shows once the
 * provider answers; the JSON-LD uses the unconditional list so it is always a subset of what is on screen.
 */
export function pricingFaq(withPayment: boolean): { q: string; a: string }[] {
  return [
    { q: t("marketing.pricing.q1"), a: t("marketing.pricing.a1") },
    { q: t("marketing.pricing.q2"), a: t("marketing.pricing.a2") },
    { q: t("marketing.pricing.q8"), a: t("marketing.pricing.a8") },
    { q: t("marketing.pricing.q3"), a: t("marketing.pricing.a3") },
    { q: t("marketing.pricing.q9"), a: t("marketing.pricing.a9", { n: PLAN_LIMITS.pro.activeWeddings, pack: PACK_WEDDINGS }) },
    { q: t("marketing.pricing.q10"), a: t("marketing.pricing.a10", { n: PLAN_LIMITS.enterprise.activeWeddings, gb: `${PLAN_LIMITS.enterprise.storageBytes / GB} GB` }) },
    { q: t("marketing.pricing.q4"), a: t("marketing.pricing.a4", { days: BILLING.withdrawalDays }) },
    { q: marketingT("platforms.planQuestion"), a: marketingT("platforms.planAnswer") },
    ...(withPayment ? [{ q: t("marketing.pricing.q6"), a: t("marketing.pricing.a6") }] : []),
    { q: t("marketing.pricing.q7"), a: t("marketing.pricing.a7", { days: RETENTION.softDeleteDays, email: OPERATOR.privacyEmail }) },
  ];
}

/** The name of a breadcrumb's parent page: a hub, a tool or the features page. */
function crumbName(path: string): string {
  if (path in CONTENT) return CONTENT[path as ContentPath].crumb;
  return TOOLS.find((tool) => tool.to === path)?.nav() ?? (path === "/features" ? t("marketing.nav.features") : path);
}

/**
 * A guide or help article (src/features/marketing/content): headline, dates, and Bridewell as author and publisher —
 * the organization, on the owner's decision (2026-11-04), until a named person reviews and signs them.
 */
export function articleLd(path: ContentPath): LdJson {
  const meta = CONTENT[path];
  const org = { "@type": "Organization", name: OPERATOR.brand, url: SITE };
  return {
    "@type": "Article",
    headline: meta.h1,
    description: meta.description,
    datePublished: meta.published,
    dateModified: meta.updated,
    inLanguage: "en",
    author: org,
    publisher: { ...org, logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
    image: `${SITE}/og/${snapshotName(path)}.jpg`,
    mainEntityOfPage: absolute(path),
  };
}

/** Head tags of a long-form page from its registry entry: breadcrumbs everywhere, an Article for guides and help. */
export function contentHead(path: ContentPath) {
  const meta = CONTENT[path];
  const crumbs = [...(meta.parent ? [{ name: crumbName(meta.parent), path: meta.parent }] : []), { name: meta.crumb, path }];
  const article = meta.kind === "guide" || meta.kind === "help";
  return pageHead({ path, title: meta.title, description: meta.description, ld: [breadcrumbLd(crumbs), ...(article ? [articleLd(path)] : [])], ogType: article ? "article" : "website" });
}

/** Per-page head() bodies, so the route files stay one line each. */
export const SEO = {
  ...(Object.fromEntries(CONTENT_PATHS.map((path) => [path, () => contentHead(path)])) as Record<ContentPath, () => ReturnType<typeof pageHead>>),
  home: () => pageHead({ path: "/", title: marketingT("seo.home.title"), description: marketingT("seo.home.description"), ld: [organizationLd(), webSiteLd(), softwareApplicationLd()] }),
  features: () => pageHead({
    path: "/features", title: marketingT("seo.features.title"), description: marketingT("seo.features.description"),
    ld: [softwareApplicationLd(), breadcrumbLd([{ name: t("marketing.nav.features"), path: "/features" }])],
  }),
  pricing: () => pageHead({
    path: "/pricing", title: marketingT("seo.pricing.title"), description: marketingT("seo.pricing.description"),
    ld: [softwareApplicationLd(), faqLd(pricingFaq(false)), breadcrumbLd([{ name: t("marketing.nav.pricing"), path: "/pricing" }])],
  }),
  contact: () => pageHead({
    path: "/contact", title: t("marketing.seo.contact.title"), description: t("marketing.seo.contact.description"),
    ld: [contactPageLd(), breadcrumbLd([{ name: t("marketing.nav.contact"), path: "/contact" }])],
  }),
  privacy: () => pageHead({ path: "/legal/privacy", title: t("marketing.seo.privacy.title"), description: t("marketing.seo.privacy.description"), ld: breadcrumbLd([{ name: t("legal.privacy"), path: "/legal/privacy" }]) }),
  terms: () => pageHead({ path: "/legal/terms", title: t("marketing.seo.terms.title"), description: t("marketing.seo.terms.description"), ld: breadcrumbLd([{ name: t("legal.terms"), path: "/legal/terms" }]) }),
  cookies: () => pageHead({ path: "/legal/cookies", title: t("marketing.seo.cookies.title"), description: t("marketing.seo.cookies.description"), ld: breadcrumbLd([{ name: t("legal.cookies"), path: "/legal/cookies" }]) }),
  deleteAccount: () => pageHead({ path: "/legal/delete-account", title: marketingT("seo.deleteAccount.title"), description: marketingT("seo.deleteAccount.description"), ld: breadcrumbLd([{ name: t("legal.deleteAccount"), path: "/legal/delete-account" }]) }),
  move: () => pageHead({
    path: "/help/move", title: marketingT("seo.move.title"), description: marketingT("seo.move.description"),
    ld: breadcrumbLd([{ name: marketingT("help.title"), path: "/help/move" }]),
  }),
  checklist: () => pageHead({
    path: "/wedding-checklist", title: marketingT("seo.checklist.title"), description: marketingT("seo.checklist.description", { n: DEFAULT_CHECKLIST.length }),
    ld: breadcrumbLd([{ name: t("marketing.tools.checklist.nav"), path: "/wedding-checklist" }]),
  }),
  budgetCalculator: () => pageHead({
    path: "/wedding-budget-calculator", title: marketingT("seo.budget.title"), description: marketingT("seo.budget.description"),
    ld: breadcrumbLd([{ name: t("marketing.tools.budget.nav"), path: "/wedding-budget-calculator" }]),
  }),
  seating: () => pageHead({
    path: "/wedding-seating-chart", title: marketingT("seo.seating.title"), description: marketingT("seo.seating.description"),
    ld: breadcrumbLd([{ name: marketingT("tools.seating.nav"), path: "/wedding-seating-chart" }]),
  }),
  guestList: () => pageHead({
    path: "/wedding-guest-list-template", title: marketingT("seo.guestList.title"), description: marketingT("seo.guestList.description"),
    ld: breadcrumbLd([{ name: marketingT("tools.guestList.nav"), path: "/wedding-guest-list-template" }]),
  }),
  invitations: () => pageHead({
    path: "/when-to-send-wedding-invitations", title: marketingT("seo.invitations.title"), description: marketingT("seo.invitations.description"),
    ld: breadcrumbLd([{ name: marketingT("tools.invitations.nav"), path: "/when-to-send-wedding-invitations" }]),
  }),
  countdown: () => pageHead({
    path: "/wedding-countdown", title: marketingT("seo.countdown.title"), description: marketingT("seo.countdown.description"),
    ld: breadcrumbLd([{ name: marketingT("tools.countdown.nav"), path: "/wedding-countdown" }]),
  }),
  dayTimeline: () => pageHead({
    path: "/wedding-day-timeline", title: marketingT("seo.timeline.title"), description: marketingT("seo.timeline.description", { n: DEFAULT_TIMELINE.length }),
    ld: breadcrumbLd([{ name: t("marketing.tools.timeline.nav"), path: "/wedding-day-timeline" }]),
  }),
};
